import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Button } from '@/components/ui/Button'
import { useSession } from '@/features/auth/api/useSession'
import {
  useClients,
  useDeleteClient,
  useMergeClients,
  useSetClientActive,
  useUpdateClient,
} from '@/features/directories/api/useDirectory'
import { ClientFormSheet, type ClientDraft } from '@/features/directories/components/DirectoryFormSheets'
import { MergeSheet } from '@/features/directories/components/MergeSheet'
import {
  directoryErrorMessage,
  referencedMessage,
  referencedRefusal,
} from '@/features/directories/directoryText'
import type { Client } from '@/types/domain'

/* The record's four acts — edit, merge, (de)activate, delete — with the refused
 * delete that NAMES the referencing quotes and cases (FR-39) and offers the
 * merge that resolves it (Story 7.1 AC 4). Moved here from the directory tab
 * unchanged in behaviour: the client's own page is where a reader is when they
 * decide to act on it.
 *
 * Affordance only. The server refuses regardless (FR-44); hiding a button
 * that would always answer 403 is kinder than offering it. */
export function ClientRecordActions({ client }: { client: Client }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: session } = useSession()
  const canManage =
    session?.role === 'super-admin' || Boolean(session?.grants?.manageClients)

  const [editing, setEditing] = useState(false)
  const [merging, setMerging] = useState(false)
  const update = useUpdateClient()
  const remove = useDeleteClient()
  const setActive = useSetClientActive()
  const merge = useMergeClients()
  // Candidates for a merge: every client the caller may see. Fetched only
  // once the sheet is asked for.
  const { data: candidates } = useClients(undefined, { enabled: merging })

  const refusal = referencedRefusal(remove.error)

  const submit = (values: ClientDraft) => {
    update.mutate({ id: client.id, payload: { ...values } }, { onSuccess: () => setEditing(false) })
  }

  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
          {t('clients.actions.edit')}
        </Button>
        {canManage ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setMerging(true)}>
              {t('clients.actions.merge')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={setActive.isPending}
              onClick={() => setActive.mutate({ id: client.id, isActive: !client.isActive })}
            >
              {client.isActive ? t('directories.deactivate') : t('directories.activate')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(client.id, { onSuccess: () => navigate('/clients', { replace: true }) })
              }
            >
              {t('directories.clients.delete')}
            </Button>
          </>
        ) : null}
      </div>

      {refusal ? (
        <Banner
          tone="attention"
          title={t('directories.clients.delete')}
          action={
            refusal.suggestion === 'merge' ? (
              <Button size="sm" variant="secondary" onClick={() => { setMerging(true); remove.reset() }}>
                {t('directories.merge.action')}
              </Button>
            ) : undefined
          }
        >
          {referencedMessage(refusal, t)}
        </Banner>
      ) : null}

      {[update, setActive, merge].map((mutation, index) =>
        mutation.error ? (
          <ErrorState key={index} error={mutation.error} onRetry={() => mutation.reset()} />
        ) : null,
      )}

      <ClientFormSheet
        open={editing}
        onClose={() => setEditing(false)}
        record={client}
        pending={update.isPending}
        onSubmit={submit}
      />

      <MergeSheet
        open={merging}
        onClose={() => setMerging(false)}
        source={merging ? client : undefined}
        candidates={candidates ?? []}
        pending={merge.isPending}
        error={merge.error ? directoryErrorMessage(merge.error, t) : undefined}
        onConfirm={(targetId) =>
          merge.mutate(
            { targetId, sourceId: client.id },
            // The source is gone after a merge; the kept record is where the
            // reader continues.
            { onSuccess: () => { setMerging(false); navigate(`/clients/${targetId}`, { replace: true }) } },
          )
        }
      />
    </>
  )
}
